import React, { useState, useEffect } from 'react';
import { HiLocationMarker, HiPencil } from 'react-icons/hi';
import { useTheme } from '../../hooks/ThemeContext';
import PostcodeService from '../../service/PostcodeService';
import { PostcodeServiceFactory } from '../../service/PostcodeServiceFactory';

// UK postcode validation regex
const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;

interface PostcodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (postcode: string) => void;
  initialPostcode?: string;
  isAreaServed: boolean | null;
}

export const PostcodeModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialPostcode = '',
  isAreaServed 
}: PostcodeModalProps) => {
  const [postcode, setPostcode] = useState(initialPostcode);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGeolocating, setIsGeolocating] = useState(false);
  const postcodeService =  PostcodeServiceFactory.getPostcodeService();
  const { theme } = useTheme();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const formattedPostcode = postcode.trim().toUpperCase();
    // if (!postcodeRegex.test(formattedPostcode)) {
    //   setError('Please enter a valid UK postcode');
    //   setIsLoading(false);
    //   return;
    // }

    try {
      // Always save if postcode is valid and close modal
      let valid = (await postcodeService.validatePostcode(formattedPostcode)).isValid;

      if (valid) {
        await onSave(formattedPostcode);
        onClose();
      }
      else {
        setError('Invalid location entered');
        setIsLoading(false);
      }

    } catch (error) {
      // Only show error for actual validation failures
      setError('An error occurred while validating your postcode');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGeolocation = async () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setIsGeolocating(true);
    setError('');

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      // Get postcode from coordinates using postcodes.io reverse geocoding
      const response = await fetch(
        `https://api.postcodes.io/postcodes?lon=${position.coords.longitude}&lat=${position.coords.latitude}`
      );
      const data = await response.json();

      if (!data.result || data.result.length === 0) {
        throw new Error('No postcode found for this location');
      }

      const nearestPostcode = data.result[0].postcode;
      setPostcode(nearestPostcode);
      // Save the postcode and close modal
      await onSave(nearestPostcode);
      onClose();
    } catch (error) {
      if (error instanceof GeolocationPositionError) {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setError('Please allow location access or enter your postcode manually');
            break;
          case error.POSITION_UNAVAILABLE:
            setError('Location information is unavailable');
            break;
          case error.TIMEOUT:
            setError('Location request timed out');
            break;
          default:
            setError('An error occurred while getting your location');
        }
      } else {
        setError('Could not determine your location. Please enter your postcode manually');
      }
    } finally {
      setIsGeolocating(false);
    }
  };

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div className="modal-content" style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '400px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{ 
          margin: '0 0 16px 0',
          fontSize: '1.5rem',
          fontWeight: '600'
        }}>
          Set Your Location
        </h2>



        <button
          onClick={handleGeolocation}
          disabled={isGeolocating || isLoading}
          style={{
            width: '100%',
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: theme.payButtonColor,
            color: theme.payButtonTextColor,
            border: 'none',
            borderRadius: '6px',
            cursor: isGeolocating ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <HiLocationMarker size={20} />
          {isGeolocating ? 'Detecting location...' : 'Use my current location'}
        </button>
        
        <div style={{ 
          textAlign: 'center', 
          margin: '16px 0',
          color: '#666'
        }}>
          Or
        </div>
        
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <input
              type="text"
              placeholder="Enter postcode"
              value={postcode}
              onChange={(e) => {
                setPostcode(e.target.value);
                setError('');
              }}
              disabled={isLoading || isGeolocating}
              style={{
                width: '90%',
                padding: '8px 12px',
                border: `1px solid ${error ? '#ef4444' : '#e2e8f0'}`,
                borderRadius: '6px',
                fontSize: '1rem',
                opacity: (isLoading || isGeolocating) ? 0.7 : 1
              }}
            />
            {error && (
              <p style={{ 
                color: '#ef4444',
                fontSize: '0.875rem',
                marginTop: '4px'
              }}>
                {error}
              </p>
            )}
          </div>
          
          <div style={{ 
            display: 'flex',
            justifyContent: 'center',
            gap: '8px'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading || isGeolocating}
              style={{
                padding: '8px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                backgroundColor: 'white',
                color: 'black',
                cursor: (isLoading || isGeolocating) ? 'not-allowed' : 'pointer',
                opacity: (isLoading || isGeolocating) ? 0.7 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || isGeolocating}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: theme.payButtonColor,
                color: theme.payButtonTextColor,
                cursor: (isLoading || isGeolocating) ? 'not-allowed' : 'pointer',
                opacity: (isLoading || isGeolocating) ? 0.7 : 1
              }}
            >
              {isLoading ? 'Checking...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface LocationBarProps {
  postcode: string;
  onEdit: () => void;
}

export const LocationBar = ({ postcode, onEdit }: LocationBarProps) => {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center'
      }}>
        <HiLocationMarker size={24} />
        <span style={{
          marginLeft: '8px',
          fontSize: '1.125rem'
        }}>
          {postcode}
        </span>
      </div>
      <div
        onClick={onEdit}
        style={{
          padding: '8px',
          border: 'none',
          borderRadius: '50%',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <HiPencil size={20} />
      </div>
    </div>
  );
};